import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { Toaster } from './components/ui/sonner';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import CreateQuotation from './pages/CreateQuotation';
import QuotationList from './pages/QuotationList';
import QuotationDetail from './pages/QuotationDetail';
import AIQuoteAssistant from './pages/AIQuoteAssistant';
import PublicQuotation from './pages/PublicQuotation';
import SubUserManager from './pages/SubUserManager';
import HotelManager from './pages/admin/HotelManager';
import HotelRateManager from './pages/admin/HotelRateManager';
import RouteManager from './pages/admin/RouteManager';
import TransferManager from './pages/admin/TransferManager';
import VehicleMaster from './pages/admin/VehicleMaster';
import SupplierManager from './pages/admin/SupplierManager';
import PlaceManager from './pages/admin/PlaceManager';
import UserManager from './pages/admin/UserManager';
import TravelAgentManager from './pages/admin/TravelAgentManager';
import ItineraryManager from './pages/admin/ItineraryManager';
import MastersManager from './pages/admin/MastersManager';
import Settings from './pages/admin/Settings';
import TenantRegister from './pages/TenantSignup';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TenantNotFound from './pages/TenantNotFound';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import FeaturesPage from './pages/FeaturesPage';
import ContactPage from './pages/ContactPage';
import B2BPartnerPortal from './pages/B2BPartnerPortal';
import B2BSettings from './pages/admin/B2BSettings';
import LineageAudit from './pages/admin/LineageAudit';
import SubscriptionPage from './pages/SubscriptionPage';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import Register from './pages/Register';
import UniversalLogin from './pages/UniversalLogin';
import './App.css';

// Tenant-aware private route
const PrivateRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { tenantSlug, buildPath, loading: tenantLoading } = useTenant();

  // Wait for both contexts to load
  if (authLoading || tenantLoading) {
    const slug = tenantSlug || (typeof window !== 'undefined' ? window.localStorage?.getItem('tenant_slug') : null);
    const tenantName = slug
      ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
      : 'your workspace';
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="app-auth-loading"
        aria-busy="true"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan mx-auto mb-4" />
          <p className="text-sm text-navy-mid font-ui">Loading {tenantName}…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={buildPath('/login')} />;
  }

  return children;
};

// Tenant-aware admin route
const AdminRoute = ({ children }) => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const { tenantSlug, buildPath, loading: tenantLoading } = useTenant();

  if (authLoading || tenantLoading) {
    const slug = tenantSlug || (typeof window !== 'undefined' ? window.localStorage?.getItem('tenant_slug') : null);
    const tenantName = slug
      ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
      : 'your workspace';
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="app-admin-loading"
        aria-busy="true"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan mx-auto mb-4" />
          <p className="text-sm text-navy-mid font-ui">Loading {tenantName}…</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to={buildPath('/login')} />;
  if (!isAdmin) return <Navigate to={buildPath('/dashboard')} />;
  
  return children;
};

// Wrapper component that validates tenant before rendering
const TenantWrapper = ({ children }) => {
  const { tenant, tenantSlug, loading, error } = useTenant();

  if (loading) {
    const slug = tenantSlug || (typeof window !== 'undefined' ? window.localStorage?.getItem('tenant_slug') : null);
    const tenantName = slug
      ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
      : 'your workspace';
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="app-tenant-loading"
        aria-busy="true"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan mx-auto mb-4" />
          <p className="text-sm text-navy-mid font-ui">Loading {tenantName}…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <TenantNotFound error={error} />;
  }

  return children;
};

// Tenant-scoped routes component
function TenantRoutes() {
  const { user } = useAuth();
  const { tenantSlug, buildPath } = useTenant();

  return (
    <TenantWrapper>
      <Routes>
        {/* B2B Partner Portal - accessible even without login */}
        <Route path="partner" element={<B2BPartnerPortal />} />
        
        {/* Forgot Password - accessible without login */}
        <Route path="forgot-password" element={<ForgotPassword />} />
        
        {/* Tenant Login */}
        <Route 
          path="login" 
          element={user ? <Navigate to={buildPath('/dashboard')} /> : <LoginPage />} 
        />
        
        {/* Change Password - requires login */}
        <Route
          path="change-password"
          element={
            <PrivateRoute>
              <ChangePassword />
            </PrivateRoute>
          }
        />

        {/* Dashboard */}
        <Route
          path="dashboard"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        
        {/* Quotation routes */}
        <Route
          path="quotations/create"
          element={
            <PrivateRoute>
              <ErrorBoundary>
                <CreateQuotation />
              </ErrorBoundary>
            </PrivateRoute>
          }
        />
        <Route
          path="quotations"
          element={
            <PrivateRoute>
              <QuotationList />
            </PrivateRoute>
          }
        />
        <Route
          path="quotations/:id"
          element={
            <PrivateRoute>
              <QuotationDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="ai-quotes"
          element={
            <PrivateRoute>
              <ErrorBoundary>
                <AIQuoteAssistant />
              </ErrorBoundary>
            </PrivateRoute>
          }
        />

        {/* Sub-user management */}
        <Route
          path="sub-users"
          element={
            <PrivateRoute>
              <SubUserManager />
            </PrivateRoute>
          }
        />

        {/* Admin routes */}
        <Route
          path="admin/hotels"
          element={
            <AdminRoute>
              <HotelManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/hotel-rates"
          element={
            <AdminRoute>
              <HotelRateManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/routes"
          element={
            <AdminRoute>
              <RouteManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/transfers"
          element={
            <AdminRoute>
              <TransferManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/vehicles"
          element={
            <AdminRoute>
              <VehicleMaster />
            </AdminRoute>
          }
        />
        <Route
          path="admin/masters"
          element={
            <AdminRoute>
              <MastersManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/suppliers"
          element={
            <AdminRoute>
              <SupplierManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/places"
          element={
            <AdminRoute>
              <PlaceManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <AdminRoute>
              <UserManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/lineage"
          element={
            <AdminRoute>
              <LineageAudit />
            </AdminRoute>
          }
        />
        <Route
          path="admin/settings"
          element={
            <AdminRoute>
              <Settings />
            </AdminRoute>
          }
        />
        <Route
          path="admin/travel-agents"
          element={
            <AdminRoute>
              <TravelAgentManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/itineraries"
          element={
            <AdminRoute>
              <ItineraryManager />
            </AdminRoute>
          }
        />
        <Route
          path="admin/b2b-settings"
          element={
            <AdminRoute>
              <B2BSettings />
            </AdminRoute>
          }
        />
        <Route
          path="subscription"
          element={
            <PrivateRoute>
              <SubscriptionPage />
            </PrivateRoute>
          }
        />

        {/* Default tenant redirect */}
        <Route path="" element={<Navigate to={buildPath('/dashboard')} />} />
        <Route path="*" element={<Navigate to={buildPath('/dashboard')} />} />
      </Routes>
    </TenantWrapper>
  );
}

function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public landing pages */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Public quote view (no tenant required) */}
        <Route path="/quote/:shareToken" element={<PublicQuotation />} />
        
        {/* Self-service registration */}
        <Route path="/register" element={<Register />} />
        <Route path="/signup" element={<Navigate to="/register" />} />
        
        {/* Universal login (email-based tenant detection) */}
        <Route path="/login" element={<UniversalLogin />} />
        
        {/* Forgot password (universal) */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        
        {/* Super admin routes at /admin */}
        <Route path="/admin/*" element={<SuperAdminDashboard />} />

        {/* Backward compatibility - redirect old routes to universal login */}
        <Route path="/dashboard" element={<Navigate to="/login" />} />
        <Route path="/quotations/*" element={<Navigate to="/login" />} />
        <Route path="/partner-login" element={<Navigate to="/login" />} />
        <Route path="/partner/*" element={<Navigate to="/login" />} />
        <Route path="/sub-users" element={<Navigate to="/login" />} />
        <Route path="/super-admin/*" element={<Navigate to="/admin" />} />

        {/* Tenant-scoped routes: /:tenantSlug/* */}
        <Route path="/:tenantSlug/*" element={<TenantRoutes />} />
      </Routes>
      <Toaster position="top-right" />
    </BrowserRouter>
  );
}

function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </TenantProvider>
  );
}

export default App;
